import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import type { AppConfig } from '../config';

type FeedbackType = 'bug' | 'idea' | 'question' | 'other';

const TYPES: { value: FeedbackType; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'idea', label: 'Idea' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
];

const MAX_FILES = 3;
const MAX_FILE_BYTES = 3 * 1024 * 1024;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
    },
  ) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type Status = 'idle' | 'sending' | 'success' | 'error';

/**
 * Feedback popup. Posts name/email/type/message + up to 3 attachments (<3 MB
 * each) to the app's feedback proxy. On web it renders a Turnstile widget and
 * sends its token; on native (no site key) it sends none. Renders only plain
 * text — no new injection surface.
 */
export function FeedbackDialog({
  open,
  onClose,
  config,
}: {
  open: boolean;
  onClose: () => void;
  config: AppConfig;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [type, setType] = useState<FeedbackType>('bug');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  const tokenRef = useRef('');
  const widgetRef = useRef<string | null>(null);
  const turnstileHostRef = useRef<HTMLDivElement>(null);
  const submitIdRef = useRef(0);
  const useTurnstile = config.turnstileSiteKey !== '';

  const close = useCallback(() => {
    submitIdRef.current += 1; // invalidate any in-flight submission
    setName('');
    setEmail('');
    setType('bug');
    setMessage('');
    setHoneypot('');
    setFiles([]);
    setStatus('idle');
    setError('');
    tokenRef.current = '';
    onClose();
  }, [onClose]);

  // Render the Turnstile widget when the dialog opens (web only).
  useEffect(() => {
    if (!open || !useTurnstile) return;
    let cancelled = false;

    const render = () => {
      const host = turnstileHostRef.current;
      if (cancelled || !host || !window.turnstile || widgetRef.current) return;
      widgetRef.current = window.turnstile.render(host, {
        sitekey: config.turnstileSiteKey,
        callback: (t) => {
          tokenRef.current = t;
        },
        'expired-callback': () => {
          tokenRef.current = '';
        },
        'error-callback': () => {
          tokenRef.current = '';
        },
        theme: 'auto',
      });
    };

    if (window.turnstile) {
      render();
    } else {
      let script = document.querySelector<HTMLScriptElement>('script[data-turnstile-script]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.dataset.turnstileScript = 'true';
        document.head.appendChild(script);
      }
      script.addEventListener('load', render, { once: true });
    }

    return () => {
      cancelled = true;
      if (widgetRef.current && window.turnstile) window.turnstile.remove(widgetRef.current);
      widgetRef.current = null;
      tokenRef.current = '';
    };
  }, [open, useTurnstile, config.turnstileSiteKey]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!open) return null;

  const onFilesChange = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length > MAX_FILES) {
      setError(`Attach at most ${String(MAX_FILES)} files.`);
      setFiles([]);
      e.target.value = '';
      return;
    }
    if (picked.some((f) => f.size > MAX_FILE_BYTES)) {
      setError('Each attachment must be under 3 MB.');
      setFiles([]);
      e.target.value = '';
      return;
    }
    setError('');
    setFiles(picked);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Enter your name.');
      return;
    }
    if (!emailPattern.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    if (!message.trim()) {
      setError('Enter a message.');
      return;
    }
    if (useTurnstile && !tokenRef.current) {
      setError('Please complete the verification.');
      return;
    }

    const submissionId = (submitIdRef.current += 1);
    setStatus('sending');
    setError('');

    const form = new FormData();
    form.append('name', name.trim());
    form.append('email', email.trim());
    form.append('type', type);
    form.append('message', message.trim());
    form.append('_gotcha', honeypot);
    if (tokenRef.current) form.append('turnstileToken', tokenRef.current);
    for (const f of files) form.append('attachments', f, f.name);

    try {
      const res = await fetch(config.feedbackEndpoint, { method: 'POST', body: form });
      // The dialog was closed (or a newer submit started) while in flight.
      if (submitIdRef.current !== submissionId) return;
      if (!res.ok) {
        setStatus('error');
        setError('We could not send your feedback. Please try again.');
        if (useTurnstile && widgetRef.current && window.turnstile) {
          window.turnstile.reset(widgetRef.current);
        }
        tokenRef.current = '';
        return;
      }
      setStatus('success');
    } catch {
      if (submitIdRef.current !== submissionId) return;
      setStatus('error');
      setError('Feedback is temporarily unavailable. Please try again later.');
    }
  };

  const sending = status === 'sending';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" role="presentation">
      <button
        type="button"
        aria-label="Close feedback"
        className="absolute inset-0 bg-black/40"
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Send feedback"
        className="mr-feedback relative w-full max-w-md rounded-xl border border-neutral-200 bg-white p-5 shadow-xl dark:border-neutral-700 dark:bg-neutral-900"
      >
        {status === 'success' ? (
          <div className="text-center">
            <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">
              Thanks for the feedback!
            </h2>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
              We read every message.
            </p>
            <button type="button" onClick={close} className="mr-btn mr-btn--primary mt-4">
              Done
            </button>
          </div>
        ) : (
          <form
            onSubmit={(e) => void submit(e)}
            className="flex flex-col gap-3 text-sm text-neutral-700 dark:text-neutral-200"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">
                Send feedback
              </h2>
              <button type="button" onClick={close} aria-label="Close" className="mr-btn">
                ✕
              </button>
            </div>

            <label className="flex flex-col gap-1">
              <span>Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mr-input"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mr-input"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span>Type</span>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as FeedbackType)}
                className="mr-input"
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span>Message</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={4}
                className="mr-input resize-y"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-neutral-600 dark:text-neutral-400">
                Attachments (optional, up to {MAX_FILES}, each &lt; 3 MB)
              </span>
              <input type="file" multiple onChange={onFilesChange} className="text-xs" />
              {files.length > 0 && (
                <span className="text-xs text-neutral-500">
                  {files.map((f) => f.name).join(', ')}
                </span>
              )}
            </label>

            {/* Honeypot: hidden from users, bots fill it. */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="absolute left-[-9999px] h-0 w-0"
            />

            {useTurnstile && <div ref={turnstileHostRef} className="min-h-[65px]" />}

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            <div className="mt-1 flex justify-end gap-2">
              <button type="button" onClick={close} className="mr-btn" disabled={sending}>
                Cancel
              </button>
              <button type="submit" className="mr-btn mr-btn--primary" disabled={sending}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
